"use client";

import Image from "next/image";
import { useState, useMemo, useRef, useEffect } from "react";
import LeadButton from "../ui/Lead/LeadButton";
import { calculateEMI, getDefaultDownPayment, EMI_DEFAULT_INTEREST_RATE, EMI_DEFAULT_TENURE } from "@/lib/utils/emi";

export interface CarPriceData {
	car_id: string;
	variant_id: string;
	name: string;
	fuelType: string;
	variant: string;
	city: string;
	exShowroomPrice: number;
	rto: number;
	insurance: number;
	optionalCharges: number;
	emiMonths: number;
	emiInterestRate: number; // annual %
	defaultOptionalChecked?: boolean; // whether "Optional" starts checked
}

interface PriceBreakupProps {
	data: CarPriceData;
}
const CDN_BASE = process.env.NEXT_PUBLIC_CDN_STATIC_URL ?? "";

function formatINR(amount: number): string {
	return "₹" + Math.round(amount).toLocaleString("en-IN");
}

/**
 * Info tooltip.
 * - Desktop (md and up): shows on hover, same as before.
 * - Mobile: has no real hover, so a tap toggles it open; tapping
 *   anywhere else on the page closes it.
 */
function InfoTooltip({ text, label }: { text: string; label: string }) {
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (!open) return;

		function handleOutsideClick(e: MouseEvent) {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		}

		document.addEventListener("click", handleOutsideClick);
		return () => document.removeEventListener("click", handleOutsideClick);
	}, [open]);

	return (
		<span
			ref={wrapperRef}
			className="group relative inline-flex items-center"
			onClick={(e) => {
				// Prevent this from bubbling to a parent <label>/checkbox
				e.stopPropagation();
				setOpen((prev) => !prev);
			}}
		>
			<Image
				src={`${CDN_BASE}search-icons/exclamatory-white.svg`}
				alt=""
				aria-hidden="true"
				width={14}
				height={14}
				className="border border-[#545454] rounded-full cursor-pointer"
			/>
			<span className="sr-only">{label}</span>
			<span
				role="tooltip"
				className={`${open ? "visible" : "invisible"
					} md:invisible md:group-hover:visible absolute z-[9999] w-[230px] p-3 ml-2 text-xs text-black bg-white border border-gray-200 rounded-lg shadow-xl left-full top-1/2 -translate-y-1/2 text-left`}
			>
				{text}
				<span className="absolute w-2 h-2 bg-white border-l border-t border-gray-200 transform rotate-45 -left-1 top-1/2 -translate-y-1/2" />
			</span>
		</span>
	);
}

export default function PriceBreakup({ data }: PriceBreakupProps) {
	const {
		name,
		fuelType,
		variant,
		city,
		exShowroomPrice,
		rto,
		insurance,
		optionalCharges,
		emiMonths,
		emiInterestRate,
		defaultOptionalChecked = true,
	} = data;

	const [optionalChecked, setOptionalChecked] = useState<boolean>(
		defaultOptionalChecked,
	);

	const onRoadPrice = useMemo(() => {
		const base = exShowroomPrice + rto + insurance;
		return optionalChecked ? base + optionalCharges : base;
	}, [optionalChecked, exShowroomPrice, rto, insurance, optionalCharges]);

	const emi = useMemo(
		() => {
			const downPayment = getDefaultDownPayment(onRoadPrice);
			return calculateEMI({
				price: onRoadPrice,
				downPayment,
				interestRate: emiInterestRate,
				tenureMonths: emiMonths,
			}).emi;
		},
		[onRoadPrice, emiMonths, emiInterestRate],
	);

	return (
		<div className="bg-white rounded-xl border border-gray-200 px-6 py-5 mt-4 md:mt-8">
			{/* Header */}
			<div className="block md:flex justify-between items-center mb-4">
				<h2 className="text-xl font-bold text-gray-900">{name} Price</h2>
				<div className="flex items-center gap-2.5">
					<span className="text-sm text-gray-400">{fuelType}</span>
					<span className="text-sm text-teal-600 border border-teal-600 rounded-md px-2.5 py-1">
						{variant}
					</span>
				</div>
			</div>

			{/* Breakdown table */}
			<table className="w-full border-collapse">
				<tbody>
					<tr className="border-b border-gray-100">
						<td className="py-2.5 text-sm text-gray-700">Ex-Showroom Price</td>
						<td className="py-2.5 text-sm font-medium text-gray-900 text-right">
							{formatINR(exShowroomPrice)}
						</td>
					</tr>

					<tr className="border-b border-gray-100">
						<td className="py-2.5 text-sm text-gray-700">RTO</td>
						<td className="py-2.5 text-sm font-medium text-gray-900 text-right">
							{formatINR(rto)}
						</td>
					</tr>

					<tr className="border-b border-gray-100">
						<td className="py-2.5 text-sm text-gray-700">
							<span className="flex items-center gap-1.5">
								Insurance
								<InfoTooltip
									label="What insurance covers"
									text="Covers theft, accidental damage, and third-party liability for the first year, as quoted by the insurer."
								/>
							</span>
						</td>
						<td className="py-2.5 text-sm font-medium text-gray-900 text-right">
							{formatINR(insurance)}
						</td>
					</tr>

					{/* <tr className="border-b border-gray-100">
						<td className="py-2.5 text-sm text-gray-700">
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									checked={optionalChecked}
									onChange={(e) => setOptionalChecked(e.target.checked)}
									className="w-4 h-4 accent-teal-600 cursor-pointer"
								/>
								Optional
								<InfoTooltip
									label="What's included in optional charges"
									text="Includes accessories, extended warranty, and dealer handling charges. Uncheck to see the price without these."
								/>
							</label>
						</td>
						<td className="py-2.5 text-sm font-medium text-gray-900 text-right">
							{formatINR(optionalCharges)}
						</td>
					</tr> */}

					<tr>
						<td colSpan={2} className="pt-3 pb-3">
							<div className="border-t border-dashed border-gray-300" />
						</td>
					</tr>

					<tr>
						<td className="text-[15px] font-bold text-gray-900">
							On-Road Price in {city}
						</td>
						<td className="text-lg font-bold text-gray-900 text-right">
							{formatINR(onRoadPrice)}
						</td>
					</tr>

					<tr>
						<td colSpan={2} className="pt-1.5">
							<div className="flex justify-center md:justify-end items-center gap-2">
								<span className="text-[13px] text-gray-700 underline">
									EMI : {formatINR(emi)}/month
								</span>
								<LeadButton
									carId={data.car_id}
									variantId={data.variant_id}
									carName={data.name}
									btnText={" View EMI Offers"}
									className="text-[13px] font-semibold text-blue-600 hover:text-blue-700"
									datatestid="car-lead-cta-button"
								/>
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			<div className="border-t border-gray-100 mt-2 md:mt-5" />

			<p className="text-xs text-teal-600 mt-3 mb-0">
				*Estimated price via verified sources. The price quote does not include
				any additional discount offered by the dealer.
			</p>
		</div>
	);
}